import { supabase } from '@/lib/supabase/client';

export const CHAT_FILES_BUCKET = 'chat-files';

/** Mirrors the bucket's file_size_limit (25 MiB) so oversized picks fail fast, client side. */
export const CHAT_FILE_MAX_SIZE = 25 * 1024 * 1024;

const SIGNED_URL_TTL_SECONDS = 60 * 60;

/** Re-sign a bit before a URL actually expires so in-flight image loads never hit a dead link. */
const SIGNED_URL_REFRESH_MARGIN_MS = 5 * 60 * 1000;

export const SIGNED_URL_REFRESH_DELAY_MS = SIGNED_URL_TTL_SECONDS * 1000 - SIGNED_URL_REFRESH_MARGIN_MS;

export type ChatFile = {
    /** Object key in the chat-files bucket: `{uploader_user_id}/{uuid}.{ext}`. */
    path: string;
    /** Original file name, shown to users and used as the download name. */
    name: string;
    type: string | null;
    size: number | null;
};

// Only types every browser can draw in an <img>. Anything else (HEIC from
// iPhones, TIFF, ...) is treated as a plain file and offered for download.
const RENDERABLE_IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/avif',
    'image/bmp',
    'image/svg+xml',
]);

export function isRenderableImage(file: Pick<ChatFile, 'type'>): boolean {
    return file.type != null && RENDERABLE_IMAGE_TYPES.has(file.type.toLowerCase());
}

/** Builds a ChatFile from the nullable columns of a message row or broadcast payload. */
export function toChatFile(
    path: string | null | undefined,
    name: string | null | undefined,
    type: string | null | undefined,
    size: number | null | undefined,
): ChatFile | null {
    if (!path || !name) return null;
    return { path, name, type: type ?? null, size: size ?? null };
}

/**
 * Storage object keys only accept a narrow ASCII set, so the key is a UUID plus a
 * sanitized extension. The original (possibly non-ASCII) name lives in the DB.
 */
export function buildChatFilePath(userId: string, file: File): string {
    const dot = file.name.lastIndexOf('.');
    const ext = dot > 0
        ? file.name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 10)
        : '';
    const id = crypto.randomUUID();
    return ext ? `${userId}/${id}.${ext}` : `${userId}/${id}`;
}

export function formatFileSize(bytes: number | null): string | null {
    if (bytes == null) return null;
    const units = ['B', 'kB', 'MB', 'GB'];
    let value = bytes;
    let unit = 0;
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit++;
    }
    const formatted = value.toLocaleString('sv-SE', { maximumFractionDigits: unit === 0 ? 0 : 1 });
    return `${formatted} ${units[unit]}`;
}

export async function uploadChatFile(userId: string, file: File): Promise<ChatFile> {
    const path = buildChatFilePath(userId, file);
    const { error } = await supabase.storage.from(CHAT_FILES_BUCKET).upload(path, file, {
        contentType: file.type || 'application/octet-stream',
        upsert: false,
    });
    if (error) throw error;

    return { path, name: file.name, type: file.type || null, size: file.size };
}

/** Best effort: a failure here only leaves an orphaned object behind, never a broken message. */
export async function removeChatFile(path: string): Promise<void> {
    const { error } = await supabase.storage.from(CHAT_FILES_BUCKET).remove([path]);
    if (error) console.error(error);
}

/**
 * Downloads through the SDK (which carries the user's auth) into a blob and hands it to
 * the browser as a same-origin object URL, so the `download` attribute is honored and
 * nothing navigates away, which matters inside the installed PWA.
 */
export async function downloadChatFile(file: ChatFile): Promise<void> {
    const { data, error } = await supabase.storage.from(CHAT_FILES_BUCKET).download(file.path);
    if (error || !data) throw error ?? new Error('Download failed');

    const blobUrl = URL.createObjectURL(data);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = file.name;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    // Give the browser time to start reading the blob before releasing it.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// --- Signed URLs for inline images ---
//
// Every image in a room asks for its URL on mount, so requests made in the same
// tick are coalesced into a single createSignedUrls call, and results are cached
// per path until shortly before they expire.

type SignedUrlCacheEntry = { url: string; expiresAt: number };
type SignedUrlResolver = (url: string | null) => void;

const signedUrlCache = new Map<string, SignedUrlCacheEntry>();
let pendingSignedUrlRequests: Map<string, SignedUrlResolver[]> | null = null;

export function getSignedChatFileUrl(path: string): Promise<string | null> {
    const cached = signedUrlCache.get(path);
    if (cached && cached.expiresAt - SIGNED_URL_REFRESH_MARGIN_MS > Date.now()) {
        return Promise.resolve(cached.url);
    }

    return new Promise(resolve => {
        if (!pendingSignedUrlRequests) {
            pendingSignedUrlRequests = new Map();
            window.setTimeout(flushSignedUrlRequests, 0);
        }
        const resolvers = pendingSignedUrlRequests.get(path) ?? [];
        resolvers.push(resolve);
        pendingSignedUrlRequests.set(path, resolvers);
    });
}

async function flushSignedUrlRequests(): Promise<void> {
    const batch = pendingSignedUrlRequests;
    pendingSignedUrlRequests = null;
    if (!batch) return;

    const paths = [...batch.keys()];
    const signedAt = Date.now();
    const { data, error } = await supabase.storage
        .from(CHAT_FILES_BUCKET)
        .createSignedUrls(paths, SIGNED_URL_TTL_SECONDS);
    if (error) console.error(error);

    const urlByPath = new Map<string, string>();
    for (const item of data ?? []) {
        if (item.path && item.signedUrl) {
            urlByPath.set(item.path, item.signedUrl);
        } else if (item.error) {
            console.error(`Could not sign chat file '${item.path}': ${item.error}`);
        }
    }

    for (const [path, resolvers] of batch) {
        const url = urlByPath.get(path) ?? null;
        if (url) {
            signedUrlCache.set(path, { url, expiresAt: signedAt + SIGNED_URL_TTL_SECONDS * 1000 });
        }
        resolvers.forEach(resolve => resolve(url));
    }
}
